import React, { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, Link } from "react-router-dom";
import Tabs from "../../Components/Tabs";
import DataTable from "react-data-table-component";
import { createColumns } from "../../helpers";
import { DATATABLE_CUSTOM_STYLES } from "../../constants/datatableStyles";
import CustomPagination from "../../Components/CustomPagination";
import PageTitle from "../../Components/UI/PageTitle";
import EyeDetailsIcon from "../../Components/Svgs/EyeDetailsIcon";
import RowLoader from "../../Components/UI/RowLoader";
import { fetchAccountsReviewList } from "../../Redux/ClientAccountsReview/AccountsReviewActions";
import { clearAccountsReviewData } from "../../Redux/ClientAccountsReview/AccountsReviewSlice";
import ErrorAnimationComponent from "../../assets/lottie/ErrorAnimation/ErrorAnimationComponent";
import { getListReviewsNumber } from "../../apis";

const AccountsReviews = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [accountsReviewsNumber, setAccountsReviewsNumber] = useState("");

  const ACCOUNTS_REVIEW_TABS = [
    {
      key: "pending",
      label: "Pending",
      number: accountsReviewsNumber?.pendingReviewAccounts,
    },
    {
      key: "needsModification",
      label: "Needs Modification",
      number: accountsReviewsNumber?.rejectedReviewAccounts,
    },
    {
      key: "confirmed",
      label: "Confirmed",
      number: accountsReviewsNumber?.confirmedReviewAccounts,
    },
  ];
  const [currentTab, setCurrentTab] = useState(
    new URLSearchParams(location.search).get("tab") || "pending"
  );
  const [searchTerm, setSearchTerm] = useState(
    new URLSearchParams(location.search).get("search") || ""
  );
  const [currentPage, setCurrentPage] = useState(
    parseInt(new URLSearchParams(location.search).get("page"), 10) || 1
  );
  const [sortKey, setSortKey] = useState(
    new URLSearchParams(location.search).get("sortKey") || ""
  );
  const [sortDirection, setSortDirection] = useState(
    new URLSearchParams(location.search).get("sortDirection") || ""
  );

  const { accountsReviewData, isLoading, pagination, error } = useSelector(
    (state) => state.AccountsReview
  );

  useEffect(() => {
    dispatch(
      fetchAccountsReviewList(
        currentTab,
        searchTerm,
        currentPage,
        sortKey,
        sortDirection
      )
    );

    return () => dispatch(clearAccountsReviewData());
  }, [currentTab, searchTerm, currentPage, sortKey, sortDirection, dispatch]);

  useEffect(() => {
    getListReviewsNumber().then((n) => {
      setAccountsReviewsNumber(n.data.data);
    });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams();
    params.set("tab", currentTab);
    if (searchTerm) params.set("search", searchTerm);
    params.set("page", currentPage);
    if (sortKey) params.set("sortKey", sortKey);
    if (sortDirection) params.set("sortDirection", sortDirection);
    navigate({ search: params.toString() });
  }, [currentTab, searchTerm, currentPage, sortKey, sortDirection, navigate]);

  const handleTabClick = (key) => {
    setCurrentTab(key);
    setCurrentPage(1); // Reset to first page on tab change
  };

  const handleFilter = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page on search change
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSortChange = (column, direction) => {
    setSortKey(column.sortKey);
    setSortDirection(direction);
    setCurrentPage(1); // Reset to first page on sort change
  };

  const filterAndSortData = () => {
    return accountsReviewData;
  };

  const hiddenColumns = ["client_id", "joined_by_id"]; // Add column keys you want to hide

  const customColumns =
    currentTab === "pending" ||
    currentTab === "needsModification" ||
    currentTab === "confirmed"
      ? [
          ...[
            {
              name: "Details",
              selector: (row) => row.name + " - custom",
              ui: (row) => (
                <div className="tw-flex tw-gap-1">
                  <Link to={`${row.client_id}`}>
                    <EyeDetailsIcon width="30px" height="30px" />
                  </Link>
                </div>
              ),
            },
          ],
        ]
      : [];

  const columns = useMemo(() => {
    return createColumns(
      accountsReviewData,
      customColumns,
      hiddenColumns,
      true
    );
  }, [accountsReviewData]).map((col) => ({
    ...col,
    sortable:
      col.name === "Client Name" ||
      col.name === "Joined At" ||
      col.name === "Joined By"
        ? true
        : false,
    width:
      col.name === "Num"
        ? "50px"
        : col.name === "Client Name"
        ? "200px"
        : "auto",

    cell:
      col.name === "Client Name"
        ? (row) => (
            <Link
              to={`/profile/${row.client_id}`}
              className="tw-underline hover:tw-text-primary"
            >
              {row.client_name}
            </Link>
          )
        : col.name === "Joined By"
        ? (row) => (
            <Link
              to={`/profile/${row.joined_by_id}`}
              className="tw-underline hover:tw-text-primary"
            >
              {row.joined_by}
            </Link>
          )
        : col.cell,
  }));

  if (error) return <ErrorAnimationComponent message={error} />;

  return (
    <>
      <PageTitle title="Accounts Review" />
      <div className="bg-white flex flex-col mt-4">
        <Tabs
          options={ACCOUNTS_REVIEW_TABS}
          onTabClick={handleTabClick}
          currentTab={currentTab}
        />

        <div className="bg-white shadow-lg rounded-lg flex flex-col p-4 gap-2 mb-5">
          <div className="relative">
            <input
              type="text"
              onChange={handleFilter}
              value={searchTerm}
              className="border border-blue-300 rounded-lg p-2 mb-2 col-md-12"
              placeholder="Search by name, mail , phone"
            />
          </div>
          <DataTable
            columns={columns}
            data={filterAndSortData()}
            customStyles={DATATABLE_CUSTOM_STYLES}
            progressPending={isLoading}
            onSort={handleSortChange}
            progressComponent={<RowLoader />}
            sortServer
          />
          <div className="flex justify-center mt-4">
            <CustomPagination
              totalPages={pagination?.last_page}
              currentPage={pagination?.current_page || 1}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default AccountsReviews;
